import type { VideoJsPlayer } from 'video.js';

import type { RampPluginOptions } from '../player.interface';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import videojs from 'video.js';

import { useLogger } from '../../../contexts/logger-context';
import { loadScript } from '../../../utils/load-script';
import { isDebugEnabled } from '../../../utils/logger';
import { SourceType } from '../player.config';

const VBRICK_SDK_SCRIPT_URL = 'https://static.eu.vbrickrev.com/vbrick-ecdn-sdk/v2/vbrick-ecdn.min.js';
const VBRICK_SDK_SCRIPT_ID = 'mi-vbrick-sdk';

export function useVBrickTech(
  enabled: boolean,
  player: VideoJsPlayer,
  setVideoSrc: (videoSrc: videojs.Tech.SourceObject[], force: boolean) => void,
  config?: RampPluginOptions,
  source?: videojs.Tech.SourceObject
) {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'VBrickTech' }), [mainLogger]);

  const sdkInstanceRef = useRef<RcvrInterface>(undefined);
  const [sdkLoaded, setSDKLoaded] = useState(false);
  const reloadSource = useCallback(() => {
    if (!enabled) {
      logger.warn('VBrick SDK :: Reloading source skipped, as the VBrick SDK is not enabled.');
      return;
    }

    if (sdkInstanceRef.current && source) {
      logger.debug('VBrick SDK :: Try to reload source.');
      setVideoSrc([{ ...source }], true);
    }
  }, [enabled, logger, source, setVideoSrc]);

  // load the VBrick SDK if not already loaded
  useEffect(() => {
    if (!enabled) {
      return;
    }
    let alive = true;

    const loadKSDK = async () => {
      logger.debug('Try loading VBrick SDK script.');
      await loadScript(VBRICK_SDK_SCRIPT_URL, VBRICK_SDK_SCRIPT_ID);

      if (!alive) {
        return;
      }
      setSDKLoaded(true);
    };

    // Don't load the SDK if it's already loaded
    if (window.RcvrInterface) {
      logger.debug('SDK already loaded, skipping load.');
      setSDKLoaded(true);
    } else {
      loadKSDK();
    }

    return () => {
      alive = false;
    };
  }, [enabled, logger]);

  // Initialize the VBrick SDK and set the media source
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!sdkLoaded || !config || !source) {
      logger.debug('Not initializing, conditions not met.');
      return;
    }

    /**
     * rifc: instance of RcvrInterface
     * src: source as determined by SDK. this will be empty when there is an error
     * fmt: may contain the source format, usually undefined
     */
    function onReady(_rifc: unknown, src: string, _fmt: unknown) {
      if (src) {
        logger.info('onReady Event :: Setting video source:', src);
        setVideoSrc([{ src, type: SourceType.HLS }], true);
      } else {
        logger.warn('onReady Event :: No source detected, cannot set video source.');
      }
    }

    function initVBrickSDK(
      player: VideoJsPlayer,
      rampOptions: RampPluginOptions,
      fallbackUrl: string,
      onReady: (rifc: unknown, src: string, fmt: unknown) => void
    ) {
      const rcvrParams = createRcvrParams(player, rampOptions, fallbackUrl, onReady);

      if (window.RcvrInterface) {
        sdkInstanceRef.current = new window.RcvrInterface(rcvrParams.i_param, rcvrParams.r_param, rcvrParams.o_param);
        logger.debug('RcvrInterface initialized successfully.', sdkInstanceRef.current);
      } else {
        logger.debug('RcvrInterface is not available, ensure the VBrick SDK script is loaded.');
      }
    }

    if (sdkInstanceRef.current) {
      logger.debug('SDK already initialized, updating source.');
      updateRamp(sdkInstanceRef.current, source, onReady);
    } else {
      logger.debug('Initializing VBrick SDK with source:', source);
      initVBrickSDK(player, config, source.src, onReady);
    }
  }, [enabled, sdkLoaded, player, config, source, setVideoSrc, logger]);

  return {
    reloadSource,
  };
}

function updateRamp(
  rampInstance: RcvrInterface,
  source: videojs.Tech.SourceObject,
  onReady: (rifc: unknown, src: string, fmt: unknown) => void
) {
  rampInstance.fallbackUrl = source.src;
  rampInstance.rcvrOnReady = (rifc: unknown, src: string, fmt: unknown) => onReady(rifc, src, fmt);
  rampInstance.fallbackProtocol(true);
}

function createRcvrParams(
  player: VideoJsPlayer,
  rampOptions: RampPluginOptions,
  fallbackUrl: string,
  onReady: (rifc: unknown, src: string, fmt: unknown) => void
) {
  const { p2pEnabled, vbrickHostName, vbrickJwtKeyName, vbrickApiKey, vbrickApiSecret, omnicacheUris, id, title } =
    rampOptions;

  // right now its not possible to disable the debug mode, wenn disabled vbrick will not initialize
  return window.RcvrInterface.GetDefaults(
    true,
    {
      id: id,
      title: title,
      player,
      playerRootName: 'videojs',
      playerRoot: videojs,
      allowXOC: p2pEnabled && !!omnicacheUris, // Will enable both OmniCache + P2P when both are provided
      verbose: true,
      allowFallback: true,
      fallbackUrl,
      analyticsUrl: '',
      allowExternStatic: false,
      debug: isDebugEnabled(),
      extern: {
        vendor: 'vbrick',
        magic: {
          hostName: vbrickHostName,
          jwtKeyName: vbrickJwtKeyName,
          apiKey: vbrickApiKey,
          apiSecret: vbrickApiSecret,
        },
      },
    },
    {
      maddr: '-1',
      onReady: (rifc: unknown, src: string, fmt: unknown) => onReady(rifc, src, fmt),
    },
    {
      verbose: true,
      uri: omnicacheUris,
    }
  );
}
